// Sales Orders (Configuration › Sales): the orders built in the configurator, each with where it stands with
// Assembly (shared domain/orderStage.ts): a draft Sales is still building; confirmed and waiting for Assembly
// to accept; accepted; in production. A confirmed order is locked for Sales: it opens read-only in the
// configurator, unless Assembly opened it for its creator to change (one revision, then it locks again).

import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { canReviseOrder, formatHelmShort, isCommittedOrder, orderStage, resolveHelm, salesOpenNow, type OrderStage, type OrderSummary, type Permission } from '@technohull/shared';
import { groupByCategory } from '../features/exportAssemblyList.js';
import { useAuth } from '../auth/AuthContext.js';
import { EmptyState, ErrorState, Fact, FactEnd, FactSentence, InlineError, LoadingState, Modal, PageHeader, SearchInput, useConfirm } from '../components/ui.js';
import { IconFile, IconLock, IconTrash, IconUnlock } from '../components/icons.js';
import { ChangeList, OpenForSalesModal, StagePill, parseChanges } from '../components/OrderWorkflow.js';
import { useDeleteOrder, useOrder, useOrders, useRelockOrder } from '../features/orders.js';
import { exportAgreementFromSnapshot } from '../features/exportSalesAgreement.js';
import { useNameResolver } from '../features/directory.js';
import { IdentityEditModal } from '../components/IdentityEditModal.js';
import { useDateFormatter, useFormatDate } from '../lib/format.js';
import { useIsPhone } from '../lib/media.js';
import { fold, hasText } from '../lib/search.js';

type Filter = 'draft' | 'waiting' | 'accepted' | 'in_production' | 'open';
const stageOf = (o: OrderSummary): OrderStage => orderStage(o);
const FILTERS: Record<Filter, (o: OrderSummary) => boolean> = {
  draft: (o) => stageOf(o) === 'draft',
  waiting: (o) => stageOf(o) === 'to_accept' || stageOf(o) === 'changed',
  accepted: (o) => stageOf(o) === 'accepted',
  in_production: (o) => stageOf(o) === 'in_production',
  open: (o) => salesOpenNow(o.salesOpen),
};
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function PreviewModal({ id, onClose }: { id: number; onClose: () => void }) {
  const { data, isLoading, error } = useOrder(id);
  const nameOf = useNameResolver();
  const df = useDateFormatter();
  const sel = data?.snapshot.equipment.filter((e) => e.selected && !e.isStandard) ?? [];
  const removed = data?.snapshot.equipment.filter((e) => e.removedFromStandard) ?? [];
  const [printErr, setPrintErr] = useState<Error | null>(null); // the agreement's print pop-up was blocked
  const o = data?.order;
  const committedRevs = data?.revisions.filter((r) => r.revision_type === 'committed') ?? [];
  return (
    <Modal
      title={`${data ? (data.snapshot.vessel?.vesselName || data.snapshot.buyer?.name || data.order.orderNumber) : 'Order'} · preview`}
      width={580}
      onClose={onClose}
      footer={
        <>
          {data && (
            <button
              className="btn"
              title="Generate the customer Boat Sale Agreement (print / save as PDF)"
              onClick={() => {
                const opened = exportAgreementFromSnapshot({ orderNumber: data.order.orderNumber, snapshot: data.snapshot, categoryOrder: data.categoryOrder });
                setPrintErr(opened ? null : new Error('The print window was blocked. Allow pop-ups for this site and try again.'));
              }}
            >
              <IconFile size={14} /> Sales agreement
            </button>
          )}
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </>
      }
    >
      {isLoading && <LoadingState />}
      {error && <ErrorState error={error} />}
      {printErr && <InlineError error={printErr} onDismiss={() => setPrintErr(null)} />}
      {data && o && (
        <>
          <div className="ao-state">
            <StagePill order={o} side="sales" />
            <p>
              {o.acceptedRevision != null
                ? <>Assembly ({nameOf(o.acceptedBy)}) accepted revision {o.acceptedRevision}{o.acceptedAt ? ` on ${df.dateTime(o.acceptedAt)}` : ''}{o.acceptedRevision !== o.revisionNumber ? `; revision ${o.revisionNumber} waits to be accepted` : ''}.</>
                : isCommittedOrder(o.status) ? <>Confirmed; Assembly hasn’t accepted it yet.</> : <>A draft: confirm it in the configurator to send it to Assembly.</>}
              {o.inProduction && <> In production since {o.productionLockedAt ? df.dateTime(o.productionLockedAt) : 'earlier'}.</>}
            </p>
          </div>
          {o.salesOpen && (
            <div className="cfg-lock is-open" role="status">
              <IconUnlock size={16} />
              <div><b>Open for {nameOf(o.createdBy)}{o.salesOpen.until ? ` until ${df.dateTime(o.salesOpen.until)}` : ''}.</b> {o.salesOpen.reason}{o.salesOpen.instructions ? `: ${o.salesOpen.instructions}` : ''}</div>
            </div>
          )}
          <dl className="ao-facts">
            <dt>Model</dt><dd>{data.snapshot.modelName ?? '-'}</dd>
            <dt>Vessel</dt><dd>{data.snapshot.vessel?.vesselName || '-'}</dd>
            <dt>Customer</dt><dd>{data.snapshot.buyer?.name || '-'}</dd>
            <dt>SAP</dt><dd>{o.sapNumber || '-'}</dd>
            <dt>Total</dt><dd>€{(data.snapshot.pricing?.total ?? 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })} incl. VAT</dd>
          </dl>

          <h4 className="ao-list-t">Options ({sel.length})</h4>
          <ul className="so-prev-list">
            {groupByCategory(sel, data.categoryOrder).slice(0, 40).map((e, i) => {
              const hs = resolveHelm(e);
              return <li key={i}>{hs ? formatHelmShort(hs) : (e.name ?? `#${e.equipmentId}`)}{e.color ? <span className="ao-color"> · {e.color}</span> : ''}{e.quantity && e.quantity > 1 ? ` × ${e.quantity}` : ''}</li>;
            })}
            {sel.length > 40 && <li className="muted">and {sel.length - 40} more</li>}
          </ul>
          {removed.length > 0 && (
            <>
              <h4 className="ao-list-t">Standard taken off ({removed.length})</h4>
              <ul className="so-prev-list is-removed">
                {groupByCategory(removed, data.categoryOrder).slice(0, 30).map((e, i) => {
                  const hs = resolveHelm(e);
                  return <li key={i}>{hs ? formatHelmShort(hs) : (e.name ?? `#${e.equipmentId}`)}</li>;
                })}
                {removed.length > 30 && <li className="muted">and {removed.length - 30} more</li>}
              </ul>
            </>
          )}

          {committedRevs.length > 0 && (
            <>
              <h4 className="ao-list-t">Revisions</h4>
              {[...committedRevs].reverse().map((r) => (
                <div key={r.id} className="ao-rev">
                  <div className="ao-rev-hd">
                    Revision {r.revision_number} · {nameOf(r.created_by)} · {df.dateTime(r.created_at)}
                    {o.acceptedRevision === r.revision_number ? ' · accepted by Assembly' : ''}
                  </div>
                  <ChangeList changes={parseChanges(r.change_summary)} />
                </div>
              ))}
            </>
          )}
        </>
      )}
    </Modal>
  );
}

export function SalesOrders() {
  const { data, isLoading, isFetching, error, refetch } = useOrders();
  const del = useDeleteOrder();
  const relock = useRelockOrder();
  const { can, me } = useAuth();
  const navigate = useNavigate();
  const fmtDate = useFormatDate();
  const df = useDateFormatter();
  const nameOf = useNameResolver();
  const [confirmNode, confirm] = useConfirm();
  const [params] = useSearchParams(); // ?q= (an email's button, Assembly Orders' "Sales →") lands on the order
  const [q, setQ] = useState(params.get('q') ?? '');
  const [filter, setFilter] = useState<Filter | null>(null);
  const [preview, setPreview] = useState<number | null>(null);
  const [editDetails, setEditDetails] = useState<number | null>(null);
  const [opening, setOpening] = useState<OrderSummary | null>(null);
  const isPhone = useIsPhone(); // phones: the whole order card opens Preview
  const allowed = (p: string) => can(p as Permission);
  const manager = can('orderlock:manage') || can('admin:users');
  const mine = (o: OrderSummary) => o.createdBy.toLowerCase() === (me?.user.email ?? '').toLowerCase();

  const all = data?.orders ?? [];
  const count = (f: Filter) => all.filter(FILTERS[f]).length;
  const rows = useMemo(() => {
    let orders = data?.orders ?? [];
    if (filter) orders = orders.filter(FILTERS[filter]);
    const query = fold(q.trim());
    if (query) orders = orders.filter((o) => hasText(query, o.orderNumber, o.sapNumber, o.vesselName, o.vesselModel, o.customer));
    return orders;
  }, [data, q, filter]);
  const toggle = (f: Filter) => setFilter((cur) => (cur === f ? null : f));
  const nDraft = count('draft');
  const nWait = count('waiting');
  const nOpen = count('open');

  return (
    <>
      <PageHeader title="Sales Orders">
        <SearchInput value={q} onChange={setQ} placeholder="Search orders" width={220} />
        {can('order:create') && <button className="btn btn-sm btn-primary" onClick={() => navigate('/configurator')}>New order</button>}
        <button className="btn btn-sm" disabled={isFetching} onClick={() => void refetch()}>{isFetching ? 'Refreshing…' : 'Refresh'}</button>
      </PageHeader>

      {data && all.length > 0 && (
        <FactSentence>
          <Fact onClick={() => toggle('draft')} pressed={filter === 'draft'}>{plural(nDraft, 'draft')}</Fact><FactEnd>,</FactEnd>{' '}
          <Fact tone={nWait ? 'warn' : undefined} onClick={() => toggle('waiting')} pressed={filter === 'waiting'}>{nWait} waiting for Assembly</Fact><FactEnd>,</FactEnd>{' '}
          <Fact tone="ok" onClick={() => toggle('accepted')} pressed={filter === 'accepted'}>{count('accepted')} accepted</Fact>
          {' '}and{' '}
          <Fact tone="info" onClick={() => toggle('in_production')} pressed={filter === 'in_production'}>{count('in_production')} in production</Fact>
          {nOpen > 0 && <><FactEnd>;</FactEnd> <Fact tone="acc" onClick={() => toggle('open')} pressed={filter === 'open'}>{plural(nOpen, 'order')} open to change</Fact></>}
          <FactEnd>.</FactEnd>
        </FactSentence>
      )}
      {(del.error ?? relock.error) && <InlineError error={del.error ?? relock.error} onDismiss={() => { del.reset(); relock.reset(); }} />}

      {isLoading && <LoadingState />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}
      {data && rows.length === 0 && (
        <EmptyState
          title={all.length ? 'No matching orders' : 'No orders'}
          hint={all.length ? 'Clear the search or the filter.' : can('order:create') ? 'Press “New order” to build one.' : undefined}
        />
      )}

      {data && rows.length > 0 && (
        <div className="tbl-wrap">
          <table className="tbl tbl-cards">
            <thead>
              <tr>
                <th>Order</th><th>SAP</th><th>Model</th><th>Vessel</th><th>Customer</th>
                <th>Stage</th><th>Rev</th><th>Created</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => {
                const committed = isCommittedOrder(o.status);
                const revise = canReviseOrder(o, allowed);
                const open = salesOpenNow(o.salesOpen);
                // a draft is its creator's; a confirmed order Assembly's (never in production)
                const canDelete = committed ? manager && !o.inProduction : can('order:delete') && (mine(o) || can('admin:users'));
                return (
                  <tr
                    key={o.id}
                    className={isPhone ? 'tc-tap' : undefined}
                    // phones: a tap on the card body is Preview (its buttons and links keep their own action)
                    onClick={isPhone ? (e) => { if (!(e.target as HTMLElement).closest('button, a')) setPreview(o.id); } : undefined}
                  >
                    <td className="tc-title so-num">{o.orderNumber}</td>
                    <td className="tc-hide so-nw">{o.sapNumber || '-'}</td>
                    <td className="tc-hide so-nw">{o.vesselModel || '-'}</td>
                    <td className="tc-hide">{o.vesselName || '-'}</td>
                    {/* the card's "vessel · model · SAP" line; the table keeps its own columns */}
                    <td className="so-sub">{[o.vesselName, o.vesselModel, o.sapNumber && `SAP ${o.sapNumber}`].filter(Boolean).join(' · ') || '-'}</td>
                    <td>{o.customer || '-'}</td>
                    <td className="tc-end so-nw">
                      <StagePill order={o} side="sales" />
                      {committed && (
                        open
                          ? <span className="so-lock is-open" title={`Open to change${o.salesOpen?.until ? ` until ${df.dateTime(o.salesOpen.until)}` : ''}`}><IconUnlock size={13} /><span className="sr-only">Open to change</span></span>
                          : <span className="so-lock" title="Locked: Assembly can open it for you"><IconLock size={13} /><span className="sr-only">Locked</span></span>
                      )}
                    </td>
                    <td className="tc-hide">{o.revisionNumber}</td>
                    <td className="muted tc-meta"><span className="so-rev">Rev {o.revisionNumber} · </span>{fmtDate(o.createdAt)} · {nameOf(o.createdBy)}</td>
                    <td className="tc-actions so-acts">
                      <button className="btn btn-sm" onClick={() => setPreview(o.id)}>Preview</button>
                      {o.status === 'draft' && can('order:create') && (mine(o) || can('admin:users')) && (
                        <button className="btn btn-sm" onClick={() => navigate(`/configurator?id=${o.id}`)}>Continue</button>
                      )}
                      {committed && can('order:create') && (
                        <button
                          className={`btn btn-sm${revise.ok && open && !manager ? ' btn-primary' : ''}`}
                          title={revise.ok ? undefined : revise.why}
                          onClick={() => navigate(`/configurator?id=${o.id}`)}
                        >{revise.ok ? 'Change' : 'View'}</button>
                      )}
                      {can('prodorders:view') && committed && (
                        <button className="btn btn-sm btn-ghost" title="Open this order in Assembly Orders" onClick={() => navigate(`/assembly-orders?q=${encodeURIComponent(o.orderNumber)}`)}>Assembly →</button>
                      )}
                      {committed && (can('order:create') || can('prodnotes:write')) && (
                        <button className="btn btn-sm btn-ghost" onClick={() => setEditDetails(o.id)}>Details</button>
                      )}
                      {committed && !o.inProduction && !open && can('salesorder:unlock') && (
                        <button className="btn btn-sm btn-ghost" onClick={() => setOpening(o)}>Open for Sales</button>
                      )}
                      {committed && open && can('salesorder:relock') && (
                        <button className="btn btn-sm btn-ghost" disabled={relock.isPending} onClick={() => relock.mutate({ id: o.id, reason: 'Locked again by Assembly' })}>Lock again</button>
                      )}
                      {canDelete && (
                        <button
                          className="btn btn-sm btn-ghost btn-del"
                          aria-label={`Delete order ${o.orderNumber}`}
                          title="Delete order"
                          disabled={del.isPending}
                          onClick={async () => {
                            const msg = committed ? `Delete confirmed order ${o.orderNumber}? Its creator and Assembly lose it from their lists (it can be restored).` : `Delete draft ${o.orderNumber}?`;
                            if (await confirm({ title: 'Delete order', message: msg, confirmLabel: 'Delete', danger: true })) del.mutate(o.id);
                          }}
                        ><IconTrash size={14} /> Delete</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {preview != null && <PreviewModal id={preview} onClose={() => setPreview(null)} />}
      {editDetails != null && <IdentityEditModal id={editDetails} onClose={() => setEditDetails(null)} />}
      {opening && <OpenForSalesModal order={opening} onClose={() => setOpening(null)} />}
      {confirmNode}
    </>
  );
}
